import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { choose } from 'lit/directives/choose.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { when } from 'lit/directives/when.js';
import { commonStyles } from '../styles/shared-styles';

export type ButtonState = 'disabled' | 'enabled' | 'invisible'

const mapToOpacity : { [key: string]: number} = {
  'disabled': 0.5,
  'enabled': 1,
  'invisible': 0,
 }

@customElement('tfp-button')
export class TFPButton extends LitElement {

    @property({ type: String })
    type : 'icon' | 'img' | 'none' = 'icon';

    @property({ type: String, reflect: true })
    variant: 'filled' | 'tonal' | 'outlined' | 'invisible' | 'text' = 'filled';

    @property({ type: String })
    state: ButtonState = 'enabled';

    @property({ type: String })
    shape: 'round' | 'rectangular' = 'rectangular';

    @property({ type: String })
    icon = '';

    @property({ type: String })
    text = '';

    @property({ type: Boolean })
    invalid = false;

    render() {
    return html`
        <button ?disabled=${this.state !== 'enabled'} class=${classMap({'with-text': this.text.length > 0 && this.type === 'none', 'with-text-icon': this.text.length > 0 && this.type !== 'none', 'invalid': this.invalid})}
                style=${styleMap({
                  opacity: mapToOpacity[this.state], pointerEvents: this.state === 'enabled' ? 'auto' : 'none',
                })}
           @click=${(e: Event) => {
             const event = new CustomEvent('tfp-action', {
               bubbles: true,
               composed: true,
             });
             this.dispatchEvent(event);
             e.stopPropagation();
           }}
        >
          ${choose(this.type, [
              [
                'icon',
                () => html`
                  <svg viewBox="0 0 100 100" style="display: inline-block; width: 70%; height: 70%;" >
                    <text x="5" y="95" fill="currentColor">${this.icon}</text>
                  </svg>
                `
              ],
              [
                'img',
                () => html`
                    <img src="${this.icon}" style="max-width: 100%; max-height: 100%; aspect-ratio: 1/1; ${this.shape === 'round' ? 'border-radius: 50%;' : ''}" alt=""></img>
                `
              ],
          ] /*,
          () => html`
              <span class="material-symbols-outlined" style="font-size: 40px;">unknown_med</span>
            ` */
          )}
          ${when(this.text.length > 0, () => html`<span style="align-content: center;"> ${this.text} </span>`)}
        </button>
    `
  }

  static styles = [commonStyles, css`
    text {
      font-size: 90px;
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      -webkit-font-feature-settings: 'liga';
      -webkit-font-smoothing: antialiased;
    }

    :host {
      display: inline-block;
      width: 100%;
      height: 100%;
      background-color: inherit;
      color: inherit;
      line-height: normal;
      overflow: hidden;
    }

    button {
      display: inline-grid;
      align-items: center;
      justify-items: center;
      justify-content: center;
      grid-template-columns: 100%;
      grid-template-rows: 100%;
      width: 100%;
      border: none;
      height: 100%;
      /* background-color: inherit; */
      color: inherit;
      font-size: inherit;
      font-family: inherit;
      font-weight: inherit;
      font-style: inherit;
      outline: none;
      padding-inline: 0px;
      padding-block: 0px;
      padding: 0;
      box-sizing: border-box;
      min-width: 0;
      line-height: inherit;
      user-select: none;
      -webkit-appearance: none;
      overflow: hidden;

      /* Material 3 Typography */
      /* font: var(--tfp-md-typescale-label-large); */

      /* Material 3 Shape */
      border-radius: var(--tfp-md-shape-corner-medium);

      /* Material 3 Motion */
      transition: background-color var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-standard),
                  color var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-standard),
                  box-shadow var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-standard),
                  opacity var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-standard),
                  border var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-standard);

    }

    /* Icon-only button adjustments */
    /*
    button:not(.with-text):not(.with-text-icon) {
      padding: 0;
      width: 40px;
      height: 40px;
      border-radius: var(--tfp-md-shape-corner-medium);
    }
    */

    button.with-text {
      display: inline-grid;
      grid-auto-flow: column;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;
      /* gap: 10px; */
      text-align: center;
      /* border: 1px solid var(--tfp-md-color-accent-green) ;
      border: 2px solid var(--tfp-md-color-primary);
      border-radius: 10px; */
      align-items: center;
      justify-items: center;
    }

    button.with-text-icon {
      display: inline-grid;
      grid-auto-flow: column;
      grid-template-columns: 50px 1fr;
      grid-template-rows: 1fr;
      /* gap: 10px; */
      text-align: center;
      /* border: 1px solid var(--tfp-md-color-accent-green); */
      /* border: 2px solid var(--tfp-md-color-primary);
      border-radius: 10px; */
      align-items: center;
      justify-items: center;
    }

    /* Filled Button (Default) */
    /* b11 design-system: dark "primary" is a light tone (text role) again, so
       the FILL paints the container pair — Kinetic solid #2563EB with white
       ink (5.17). (Before: white on #3700B3 measured 11.58 only because the
       fill was the invisible-purple defect the audit fixed.) */
    button {
      background-color: var(--tfp-md-color-brand-fill);
      color: var(--tfp-md-color-on-primary);
    }
    button:hover {
      box-shadow: var(--tfp-md-elevation-level-1);
      opacity: 0.92; /* Simple hover state */
    }
    button:active {
      opacity: 0.88; /* Simple active state */
    }

  /* Invalid State */
    button.invalid {
      border: 2px solid var(--tfp-md-color-error) !important;
      /* Optional: Change text/icon color to error?
         M3 doesn't typically have invalid buttons, but if we do, a red border is clear.
      */
    }


    /* Tonal Button */
    :host([variant="tonal"]) button {
      background-color: var(--tfp-md-color-secondary-container);
      color: var(--tfp-md-color-on-secondary-container);
    }

    /* Outlined Button */
    :host([variant="outlined"]) button {
      background-color: transparent;
      color: var(--tfp-md-color-primary);
      border: 1px solid var(--tfp-md-color-outline);
    }
    :host([variant="outlined"]) button:hover {
      background-color: color-mix(in srgb, var(--tfp-md-color-primary), transparent 92%);
      border: 1px solid var(--tfp-md-color-primary);
    }

    /* Invisible Button */
    :host([variant="invisible"]) button {
      background-color: transparent;
      color: var(--tfp-md-color-on-primary);
      border: none;
    }
    :host([variant="invisible"]) button:hover {
      background-color: color-mix(in srgb, var(--tfp-md-color-primary), transparent 92%);
      box-shadow: none;
    }

    /* Text Button — transparent, primary-colored label (M3 text button) */
    :host([variant="text"]) button {
      background-color: transparent;
      color: var(--tfp-md-color-primary);
      padding: 0 12px;
    }
    :host([variant="text"]) button:hover {
      background-color: color-mix(in srgb, var(--tfp-md-color-primary), transparent 92%);
      box-shadow: none;
    }
    /* Disabled State — contrast audit fix #6 floor: the .5-opacity blur made a
       filled button vanish (composite 1.03). Fill stays visible at .38 over
       the surface plus a 3.0-compliant outline ring (box-shadow, so no layout
       shift); blur kept as the extra affordance. WCAG-exempt, but it must
       still exist. */
    button:disabled {
      filter: blur(1px);
      background-color: color-mix(in srgb, var(--tfp-md-color-brand-fill) 38%, var(--tfp-md-color-surface));
      box-shadow: inset 0 0 0 1.5px var(--tfp-md-color-outline);
      /*
      background-color: color-mix(in srgb, var(--tfp-md-color-on-surface), transparent 88%);
      color: color-mix(in srgb, var(--tfp-md-color-on-surface), transparent 62%);
      cursor: not-allowed;
      box-shadow: none;
      border-color: transparent;
      */
    }
    /* Ghost variants keep their own disabled look: no fill wash, no ring. */
    :host([variant="tonal"]) button:disabled,
    :host([variant="outlined"]) button:disabled,
    :host([variant="text"]) button:disabled,
    :host([variant="invisible"]) button:disabled {
      background-color: transparent;
      box-shadow: none;
    }
    :host([variant="tonal"]) button:disabled {
      background-color: color-mix(in srgb, var(--tfp-md-color-secondary-container) 38%, var(--tfp-md-color-surface));
    }

    /*
    :host([variant="outlined"]) button:disabled {
      background-color: transparent;
      border-color: color-mix(in srgb, var(--tfp-md-color-on-surface), transparent 88%);
    }
    :host([variant="text"]) button:disabled {
      background-color: transparent;
    }
    */

  `,
  ]
}

declare global {
  interface HTMLElementTagNameMap {
    'tfp-button': TFPButton
  }
}
